import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, CheckCircle2, Target, Flame, Utensils, Activity, Lock } from "lucide-react";
import { toast } from "sonner";

const GOAL_LABELS = {
  WEIGHT_LOSS: "Weight Loss",
  MUSCLE_GAIN: "Muscle Gain",
  STRENGTH_POWER: "Strength & Powerlifting",
  ATHLETIC_HYROX: "Athletic Performance (Hyrox / Sports)",
  COMBAT_MMA: "Combat Sport (MMA)",
  GENERAL_FITNESS: "General Fitness & Wellness",
  REHAB: "Rehabilitation / Injury Recovery",
  OTHER: "Other",
};

/* ==================== Pending Documents ==================== */
export function PendingDocsPanel() {
  const qc = useQueryClient();
  const [openDoc, setOpenDoc] = useState(null);
  const [sigText, setSigText] = useState("");
  const { data: pending = [] } = useQuery({
    queryKey: ["policy-pending"],
    queryFn: async () => (await apiClient.get("/policy-docs/pending")).data,
  });
  const sign = useMutation({
    mutationFn: async () => (await apiClient.post(`/policy-docs/${openDoc.id}/sign`, { signature_text: sigText })).data,
    onSuccess: () => { toast.success("Signed. Thank you."); qc.invalidateQueries({ queryKey: ["policy-pending"] }); setOpenDoc(null); setSigText(""); },
    onError: () => toast.error("Sign failed"),
  });
  if (pending.length === 0) return null;
  return (
    <>
      <Panel data-testid="pending-docs-panel" className="border-red-500/30">
        <PanelHeader
          title={`📄 ${pending.length} document${pending.length > 1 ? "s" : ""} need your signature`}
          subtitle="Please review and sign to keep your account current"
        />
        <div className="space-y-2">
          {pending.map((d) => (
            <div key={d.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center justify-between gap-3" data-testid={`pending-doc-${d.slug}`}>
              <div>
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-volt" />
                  <span className="font-semibold">{d.title}</span>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">v{d.current_version}</span>
                </div>
                {d.last_signed_version > 0 && (
                  <div className="text-[10px] text-zinc-500 mt-1">You last signed v{d.last_signed_version} — updated since.</div>
                )}
              </div>
              <Button size="sm" onClick={() => setOpenDoc(d)} className="bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em]" data-testid={`sign-btn-${d.slug}`}>
                Review & Sign
              </Button>
            </div>
          ))}
        </div>
      </Panel>
      <Dialog open={!!openDoc} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{openDoc?.title}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Version {openDoc?.current_version}</DialogDescription>
          </DialogHeader>
          <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-zinc-300 border border-zinc-900 rounded-sm p-4 bg-black" data-testid="doc-body">
            {openDoc?.body_markdown}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Type your full name to sign</Label>
            <Input value={sigText} onChange={(e) => setSigText(e.target.value)} placeholder="Your name" data-testid="sig-input" className="bg-black border-zinc-800 rounded-sm mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDoc(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => sign.mutate()} disabled={!sigText.trim() || sign.isPending} className="bg-volt hover:bg-volt-hover text-black rounded-sm" data-testid="confirm-sign">
              <CheckCircle2 size={14} className="mr-1" /> Sign v{openDoc?.current_version}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ==================== Goal Setup + Locked View ==================== */
export function GoalPanel({ forceOpen = false, onSaved }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["my-goal"],
    queryFn: async () => (await apiClient.get("/me/goal")).data,
  });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [other, setOther] = useState("");
  const shouldPrompt = data && !data.goal_type;
  const modalOpen = open || forceOpen || shouldPrompt;

  const set = useMutation({
    mutationFn: async () => (await apiClient.patch("/me/goal", { goal_type: selected, goal_other: selected === "OTHER" ? other : null })).data,
    onSuccess: () => { toast.success("Goal locked in for 6 months. 💪"); qc.invalidateQueries({ queryKey: ["my-goal"] }); setOpen(false); onSaved?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const lockedUntil = data?.goal_locked_until;
  const goalLabel = data?.goal_type === "OTHER" ? data.goal_other : GOAL_LABELS[data?.goal_type];

  return (
    <>
      <Panel data-testid="goal-panel">
        <PanelHeader title="Your Goal" subtitle={data?.goal_type ? "Locked for 6 months from the day you set it" : "Set your training north star"} />
        {data?.goal_type ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Target size={28} className="text-volt" />
              <div>
                <div className="font-display text-2xl font-black" data-testid="goal-label">{goalLabel}</div>
                {lockedUntil && (
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-1 mt-1">
                    <Lock size={10} /> Locked until {new Date(lockedUntil).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
            </div>
            <Button variant="outline" disabled={!data?.can_change} onClick={() => setOpen(true)} data-testid="change-goal-btn" className="rounded-sm text-[10px] uppercase tracking-[0.16em]">
              {data?.can_change ? "Change" : "Locked"}
            </Button>
          </div>
        ) : (
          <div className="text-zinc-500 text-sm">Set your goal to unlock personalised programming.</div>
        )}
      </Panel>
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!shouldPrompt && !o) setOpen(false); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-md" data-testid="goal-dialog"
          onInteractOutside={(e) => { if (shouldPrompt) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (shouldPrompt) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Set your training goal</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Locked for 6 months once you save.</DialogDescription>
          </DialogHeader>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="goal-select" className="bg-black border-zinc-800 rounded-sm"><SelectValue placeholder="Pick a goal" /></SelectTrigger>
            <SelectContent>
              {(data?.options || Object.keys(GOAL_LABELS)).map((g) => (<SelectItem key={g} value={g}>{GOAL_LABELS[g] || g}</SelectItem>))}
            </SelectContent>
          </Select>
          {selected === "OTHER" && (
            <Input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Describe your goal" data-testid="goal-other-input" className="bg-black border-zinc-800 rounded-sm" />
          )}
          <DialogFooter>
            <Button onClick={() => set.mutate()} disabled={!selected || (selected === "OTHER" && !other.trim())}
              data-testid="save-goal-btn" className="bg-volt hover:bg-volt-hover text-black rounded-sm w-full">Lock In</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ==================== Effort & Fuel Scores ==================== */
function ScoreTile({ label, icon: Icon, avg, count, value, onSubmit, testid }) {
  const [v, setV] = useState(value || 5);
  const color = avg == null ? "#71717A" : avg >= 8 ? "#00E676" : avg >= 5 ? "#CCFF00" : "#FF3B30";
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500">
          <Icon size={14} className="text-volt" /> {label}
        </div>
        <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-600">7-day avg</div>
      </div>
      <div className="font-display text-5xl font-black mt-2" style={{ color }} data-testid={`${testid}-avg`}>
        {avg ?? "—"}
      </div>
      <div className="text-[10px] text-zinc-500 mt-1">{count} log{count === 1 ? "" : "s"} in last 7 days</div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <input type="range" min="1" max="10" value={v} onChange={(e) => setV(Number(e.target.value))}
            data-testid={`${testid}-slider`}
            className="flex-1 accent-[#CCFF00]" />
          <div className="font-display text-2xl font-bold w-8 text-right">{v}</div>
        </div>
        <Button size="sm" onClick={() => onSubmit(v)} data-testid={`${testid}-submit`}
          className="w-full h-8 bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em]">
          Log Today
        </Button>
      </div>
    </div>
  );
}

export function EffortFuelPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["scores-summary"],
    queryFn: async () => (await apiClient.get("/scores/summary")).data,
  });
  const effort = useMutation({
    mutationFn: async (score) => (await apiClient.post("/scores/effort", { score })).data,
    onSuccess: () => { toast.success("Effort logged"); qc.invalidateQueries({ queryKey: ["scores-summary"] }); },
  });
  const fuel = useMutation({
    mutationFn: async (score) => (await apiClient.post("/scores/fuel", { score })).data,
    onSuccess: () => { toast.success("Fuel logged"); qc.invalidateQueries({ queryKey: ["scores-summary"] }); },
  });
  return (
    <Panel data-testid="effort-fuel-panel">
      <PanelHeader title="How was today?" subtitle="Log Effort after your workout · Log Fuel before bed" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ScoreTile label="Effort Score" icon={Flame} avg={data?.effort_avg} count={data?.effort_count || 0} onSubmit={effort.mutate} testid="effort" />
        <ScoreTile label="Fuel Score" icon={Utensils} avg={data?.fuel_avg} count={data?.fuel_count || 0} onSubmit={fuel.mutate} testid="fuel" />
      </div>
    </Panel>
  );
}

/* ==================== Assessments ==================== */
export function AssessmentPanel({ userId }) {
  const { data = [] } = useQuery({
    enabled: !!userId,
    queryKey: ["member-assessments", userId],
    queryFn: async () => (await apiClient.get(`/members/${userId}/assessments`)).data,
  });
  const latest = data[0];
  const prev = data[1];
  const arrow = (curr, previous, inverse = false) => {
    if (curr == null || previous == null) return null;
    const up = curr > previous;
    const better = inverse ? !up : up;
    return <span style={{ color: better ? "#00E676" : "#FF3B30" }} className="text-[10px] ml-1">{up ? "▲" : "▼"}</span>;
  };
  const cell = (label, key, section, unit = "", inverseIsBetter = false) => {
    const c = latest?.[section]?.[key];
    const p = prev?.[section]?.[key];
    if (c == null) return null;
    return (
      <div key={`${section}-${key}`} className="bg-black border border-zinc-900 rounded-sm p-2.5">
        <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
        <div className="font-display text-xl font-black mt-0.5">{c}{unit && <span className="text-xs text-zinc-500 ml-0.5">{unit}</span>}{arrow(c, p, inverseIsBetter)}</div>
      </div>
    );
  };
  return (
    <Panel data-testid="assessment-panel">
      <PanelHeader title="Latest Assessment"
        subtitle={latest ? `Recorded ${new Date(latest.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : "No assessment yet — your coach will add one"} />
      {!latest ? (
        <div className="text-zinc-500 text-sm py-4">Your coach will log body composition, strength, endurance, and 1RM benchmarks here.</div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold mb-2">Body Composition</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {cell("Weight", "weight_kg", "body_composition", " kg", true)}
              {cell("Body Fat", "bf_pct", "body_composition", " %", true)}
              {cell("Muscle Mass", "muscle_mass_kg", "body_composition", " kg")}
              {cell("BMI", "bmi", "body_composition", "", true)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold mb-2">Strength (reps)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {cell("Pushups", "pushup_max", "strength")}
              {cell("Pullups", "pullup_max", "strength")}
              {cell("Plank", "plank_sec", "strength", " s")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold mb-2">1 Rep Max (kg)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {cell("Bench", "bench_kg", "one_rm")}
              {cell("Squat", "squat_kg", "one_rm")}
              {cell("Deadlift", "deadlift_kg", "one_rm")}
              {cell("OH Press", "oh_press_kg", "one_rm")}
              {cell("Clean", "clean_kg", "one_rm")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold mb-2">Endurance</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {cell("2km Row", "row_2k_sec", "endurance", " s", true)}
              {cell("500m Sprint", "sprint_500m_sec", "endurance", " s", true)}
              {cell("VO2 Max est.", "vo2_max_est", "endurance", "")}
            </div>
          </div>
          {latest.notes && (
            <div className="bg-black border border-zinc-900 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Coach Notes</div>
              <div className="text-sm text-zinc-300 mt-1">{latest.notes}</div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
